import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../../services';
import { utils } from '../../../utils';
import FormLayout from '../../../components/Common/FormLayout';

interface FuelingFormData {
  veiculo_id: number | '';
  motorista_id: number | '';
  data_inicio: string;
  data_fim: string;
  volume: number | '';
  custo: number | '';
  valor_unitario: number | '';
  odometro: number | '';
  tipo_combustivel: string;
  posto_nome: string;
  posto_cnpj: string;
  observacoes: string;
  status: 'concluido' | 'pendente' | 'cancelado';
}

interface Vehicle {
  id: number;
  placa: string;
  modelo: string;
  marca: string;
}

interface Driver {
  id: number;
  nome: string;
  cpf: string;
}

const FuelingCreate: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FuelingFormData>({
    veiculo_id: '',
    motorista_id: '',
    data_inicio: '',
    data_fim: '',
    volume: '',
    custo: '',
    valor_unitario: '',
    odometro: '',
    tipo_combustivel: 'Diesel S10',
    posto_nome: '',
    posto_cnpj: '',
    observacoes: '',
    status: 'concluido'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setIsLoadingData(true);

      // Carregar veículos
      const vehiclesResponse = await apiService.getVehicles({ limit: 1000 });
      if (vehiclesResponse.success && vehiclesResponse.data) {
        setVehicles(vehiclesResponse.data);
      }

      // Carregar motoristas
      const driversResponse = await apiService.getDrivers({ limit: 1000 });
      if (driversResponse.success && driversResponse.data) {
        setDrivers(driversResponse.data);
      }
    } catch (error: any) {
      console.error('Erro ao carregar dados iniciais:', error);
      utils.showNotification('Erro ao carregar veículos e motoristas', { type: 'error' });
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    // Calcular valor unitário automaticamente se volume e custo forem preenchidos
    if (name === 'volume' || name === 'custo') {
      const volume = name === 'volume' ? Number(value) : Number(formData.volume);
      const custo = name === 'custo' ? Number(value) : Number(formData.custo);

      if (volume > 0 && custo > 0) {
        const valorUnitario = custo / volume;
        setFormData(prev => ({
          ...prev,
          [name]: value,
          valor_unitario: Number(valorUnitario.toFixed(3))
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[name as keyof FuelingFormData]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.veiculo_id) {
      newErrors.veiculo_id = 'Veículo é obrigatório';
    }

    if (!formData.data_inicio) {
      newErrors.data_inicio = 'Data de início é obrigatória';
    }

    if (!formData.volume || Number(formData.volume) <= 0) {
      newErrors.volume = 'Volume deve ser maior que zero';
    }

    if (!formData.tipo_combustivel.trim()) {
      newErrors.tipo_combustivel = 'Tipo de combustível é obrigatório';
    }

    if (formData.data_fim && formData.data_inicio) {
      const dataInicio = new Date(formData.data_inicio);
      const dataFim = new Date(formData.data_fim);
      if (dataFim < dataInicio) {
        newErrors.data_fim = 'Data de fim não pode ser anterior à data de início';
      }
    }

    if (formData.custo && formData.volume) {
      const custo = Number(formData.custo);
      const volume = Number(formData.volume);
      if (custo < 0) {
        newErrors.custo = 'Custo não pode ser negativo';
      }
      if (volume > 0 && custo > 0) {
        const valorCalculado = custo / volume;
        if (Math.abs(valorCalculado - Number(formData.valor_unitario)) > 0.01) {
          // Recalcular automaticamente
          setFormData(prev => ({
            ...prev,
            valor_unitario: Number(valorCalculado.toFixed(3))
          }));
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      utils.showNotification('Por favor, corrija os erros no formulário', { type: 'error' });
      return;
    }

    setIsLoading(true);
    try {
      const submitData = {
        veiculo_id: Number(formData.veiculo_id),
        motorista_id: formData.motorista_id ? Number(formData.motorista_id) : undefined,
        data_inicio: formData.data_inicio,
        data_fim: formData.data_fim || undefined,
        volume: Number(formData.volume),
        custo: formData.custo ? Number(formData.custo) : undefined,
        valor_unitario: formData.valor_unitario ? Number(formData.valor_unitario) : undefined,
        odometro: formData.odometro ? Number(formData.odometro) : undefined,
        tipo_combustivel: formData.tipo_combustivel,
        posto_nome: formData.posto_nome || undefined,
        posto_cnpj: formData.posto_cnpj || undefined,
        observacoes: formData.observacoes || undefined,
        status: formData.status
      };

      const response = await apiService.createFueling(submitData);

      if (response.success) {
        utils.showNotification('Abastecimento cadastrado com sucesso!', { type: 'success' });
        navigate('/abastecimentos');
      } else {
        throw new Error(response.message || 'Erro ao cadastrar abastecimento');
      }
    } catch (error: any) {
      console.error('Erro ao cadastrar abastecimento:', error);
      utils.showNotification(error.message || 'Erro ao cadastrar abastecimento', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = () => {
    const fakeEvent = {
      preventDefault: () => {}
    } as React.FormEvent;
    handleSubmit(fakeEvent);
  };

  const handleCancel = () => {
    navigate('/abastecimentos');
  };

  if (isLoadingData) {
    return (
      <FormLayout
        title="Carregando..."
        isLoading={true}
        onCancel={handleCancel}
        showActions={false}
      >
        <div></div>
      </FormLayout>
    );
  }

  return (
    <FormLayout
      title="Novo Abastecimento"
      subtitle="Cadastro de Abastecimento"
      onCancel={handleCancel}
      onSave={handleSave}
      saveButtonText="Cadastrar Abastecimento"
      isLoading={isLoading}
      showActions={false}
    >
      <form onSubmit={handleSubmit}>
        {/* Informações Principais */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Informações Principais
          </h3>

          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="form-group">
              <label className="form-label">Veículo *</label>
              <select
                name="veiculo_id"
                value={formData.veiculo_id}
                onChange={handleInputChange}
                className={`form-control ${errors.veiculo_id ? 'error' : ''}`}
              >
                <option value="">Selecione um veículo</option>
                {vehicles.map(vehicle => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.placa} - {vehicle.marca} {vehicle.modelo}
                  </option>
                ))}
              </select>
              {errors.veiculo_id && <span className="error-message">{errors.veiculo_id}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Motorista</label>
              <select
                name="motorista_id"
                value={formData.motorista_id}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="">Selecione um motorista (opcional)</option>
                {drivers.map(driver => (
                  <option key={driver.id} value={driver.id}>
                    {driver.nome} - CPF: {driver.cpf}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Data/Hora de Início *</label>
              <input
                type="datetime-local"
                name="data_inicio"
                value={formData.data_inicio}
                onChange={handleInputChange}
                className={`form-control ${errors.data_inicio ? 'error' : ''}`}
              />
              {errors.data_inicio && <span className="error-message">{errors.data_inicio}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Data/Hora de Fim</label>
              <input
                type="datetime-local"
                name="data_fim"
                value={formData.data_fim}
                onChange={handleInputChange}
                className={`form-control ${errors.data_fim ? 'error' : ''}`}
              />
              {errors.data_fim && <span className="error-message">{errors.data_fim}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="concluido">Concluído</option>
                <option value="pendente">Pendente</option>
                <option value="cancelado">Cancelado</option>
              </select>
            </div>
          </div>
        </div>

        {/* Dados do Abastecimento */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Dados do Abastecimento
          </h3>

          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="form-group">
              <label className="form-label">Volume (Litros) *</label>
              <input
                type="number"
                name="volume"
                value={formData.volume}
                onChange={handleInputChange}
                className={`form-control ${errors.volume ? 'error' : ''}`}
                placeholder="Ex: 120.5"
                min="0"
                step="0.01"
              />
              {errors.volume && <span className="error-message">{errors.volume}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Custo Total (R$)</label>
              <input
                type="number"
                name="custo"
                value={formData.custo}
                onChange={handleInputChange}
                className={`form-control ${errors.custo ? 'error' : ''}`}
                placeholder="Ex: 830.25"
                min="0"
                step="0.01"
              />
              {errors.custo && <span className="error-message">{errors.custo}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Valor Unitário (R$/L)</label>
              <input
                type="number"
                name="valor_unitario"
                value={formData.valor_unitario}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Ex: 6.89"
                min="0"
                step="0.001"
                readOnly={!!(formData.volume && formData.custo)}
              />
              <small style={{ color: '#7f8c8d', fontSize: '0.85em' }}>
                {formData.volume && formData.custo ? 'Calculado automaticamente' : 'Preencha volume e custo para calcular'}
              </small>
            </div>

            <div className="form-group">
              <label className="form-label">Tipo de Combustível *</label>
              <select
                name="tipo_combustivel"
                value={formData.tipo_combustivel}
                onChange={handleInputChange}
                className={`form-control ${errors.tipo_combustivel ? 'error' : ''}`}
              >
                <option value="Diesel S10">Diesel S10</option>
                <option value="Diesel S500">Diesel S500</option>
                <option value="Gasolina Comum">Gasolina Comum</option>
                <option value="Gasolina Aditivada">Gasolina Aditivada</option>
                <option value="Etanol">Etanol</option>
                <option value="GNV">GNV</option>
                <option value="Arla 32">Arla 32</option>
              </select>
              {errors.tipo_combustivel && <span className="error-message">{errors.tipo_combustivel}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Odômetro (km)</label>
              <input
                type="number"
                name="odometro"
                value={formData.odometro}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Ex: 125430"
                min="0"
                step="1"
              />
            </div>
          </div>
        </div>

        {/* Dados do Posto */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Dados do Posto
          </h3>

          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="form-group">
              <label className="form-label">Nome do Posto</label>
              <input
                type="text"
                name="posto_nome"
                value={formData.posto_nome}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Ex: Posto Shell"
              />
            </div>

            <div className="form-group">
              <label className="form-label">CNPJ do Posto</label>
              <input
                type="text"
                name="posto_cnpj"
                value={formData.posto_cnpj}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Ex: 12.345.678/0001-90"
                maxLength={18}
              />
            </div>
          </div>
        </div>

        {/* Observações */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Observações
          </h3>

          <div className="form-group">
            <label className="form-label">Observações</label>
            <textarea
              name="observacoes"
              value={formData.observacoes}
              onChange={handleInputChange}
              className="form-control"
              placeholder="Informações adicionais sobre o abastecimento..."
              rows={4}
            />
          </div>
        </div>

        {/* Ações do Formulário */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="loading"></span>
                Cadastrando...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Cadastrar Abastecimento
              </>
            )}
          </button>
        </div>
      </form>
    </FormLayout>
  );
};

export default FuelingCreate;
